import React, { useEffect, useState } from "react";
import { getWavesCutover, updateWaveItem, wavesSignoff } from "../lib/api";
import { toast } from "sonner";
import { PrintButton } from "../components/PrintButton";
import {
  Rocket, ChevronRight, ChevronDown, CheckCircle2, Circle, Lock, ShieldCheck, AlertTriangle,
} from "lucide-react";

const MIG = {
  pending: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  onboarded: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  cutover: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
};

export default function WaveCutover() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () => getWavesCutover().then((d) => { setData(d); if (open === null && d.waves[0]) setOpen(d.waves[0].wave); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleItem = async (wave, itemId, checked) => {
    setData((p) => ({
      ...p,
      waves: p.waves.map((w) => w.wave === wave
        ? { ...w, checklist: { ...w.checklist, [itemId]: { ...(w.checklist[itemId] || {}), checked } } } : w),
    }));
    try { await updateWaveItem(wave, { item_id: itemId, checked }); }
    catch { toast.error("Save failed"); }
  };

  const signoff = async (wave) => {
    const approver = window.prompt(`Approver to sign off cutover for "${wave}":`, "");
    if (approver === null) return;
    try {
      await wavesSignoff(wave, { signed_off: true, approver });
      toast.success(`${wave} cutover signed off`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sign-off blocked"); }
  };
  const reopen = async (wave) => {
    try { await wavesSignoff(wave, { signed_off: false }); toast.info(`${wave} reopened`); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> waves <ChevronRight className="h-3 w-3" /> cutover day
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-4xl">
        <div className="no-print flex justify-end mb-4"><PrintButton label="Print Runsheet" /></div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <Rocket className="h-4 w-4" /> Wave Cutover
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Cutover-Day Runsheet</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Run and sign off an entire migration wave together. A wave can only be signed off once every
          cutover-day item is checked and every site in it has its per-site Cutover gate approved.
        </p>

        {!data ? (
          <div className="mt-8 text-zinc-500 font-mono text-sm">Loading waves…</div>
        ) : (
          <div className="mt-6 space-y-3" data-testid="wave-cutover-list">
            {data.waves.map((w) => {
              const isOpen = open === w.wave;
              const checked = data.template.filter((t) => w.checklist[t.id]?.checked).length;
              const ready = checked === data.template.length && w.all_sites_cutover;
              return (
                <div key={w.wave} data-testid={`wave-${w.wave}`} className={`rounded-lg border bg-zinc-900 overflow-hidden ${w.signed_off ? "border-emerald-600/30" : "border-zinc-800"}`}>
                  <button onClick={() => setOpen(isOpen ? null : w.wave)} data-testid={`wave-toggle-${w.wave}`} className="w-full flex items-center gap-3 px-4 py-3.5 text-left">
                    <span className="h-8 w-8 rounded-md bg-blue-600/15 border border-blue-600/30 flex items-center justify-center shrink-0">
                      <Rocket className="h-4 w-4 text-blue-400" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-white">{w.wave}</span>
                        {w.signed_off && <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />}
                      </div>
                      <div className="text-[11px] text-zinc-500">{w.sites.length} site(s) · {w.sites.filter((s) => s.cutover_approved).length} cutover-approved</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-[13px] font-mono ${checked === data.template.length ? "text-emerald-400" : "text-zinc-400"}`}>{checked}/{data.template.length}</div>
                      <div className="text-[10px] text-zinc-600 uppercase tracking-wider">checklist</div>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-zinc-500 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isOpen && (
                    <div className="p-4 border-t border-zinc-800">
                      {/* Member sites */}
                      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Sites in this wave</div>
                      <div className="grid sm:grid-cols-2 gap-1.5 mb-4">
                        {w.sites.map((s) => (
                          <div key={s.site_id} data-testid={`wave-site-${s.order}`} className="flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5">
                            <span className="text-[12px] text-zinc-300 truncate">{s.name}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${MIG[s.mig_status] || ""}`}>{s.mig_status}</span>
                              {s.cutover_approved
                                ? <span className="flex items-center gap-1 text-[11px] text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> gate</span>
                                : <span className="flex items-center gap-1 text-[11px] text-zinc-500"><Lock className="h-3 w-3" /> gate</span>}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Cutover-day checklist */}
                      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Cutover-day checklist</div>
                      <div className="space-y-1.5">
                        {data.template.map((t) => {
                          const on = w.checklist[t.id]?.checked;
                          return (
                            <button key={t.id} onClick={() => toggleItem(w.wave, t.id, !on)} disabled={w.signed_off}
                              data-testid={`wave-item-${w.wave}-${t.id}`}
                              className="w-full flex items-center gap-2.5 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-left disabled:opacity-70">
                              {on ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> : <Circle className="h-4 w-4 text-zinc-600 shrink-0" />}
                              <span className={`text-[13px] ${on ? "text-zinc-200" : "text-zinc-400"}`}>{t.label}</span>
                            </button>
                          );
                        })}
                      </div>

                      {!w.all_sites_cutover && (
                        <div className="mt-3 flex items-start gap-2 text-[12px] text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
                          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> Not all sites in this wave have their Cutover gate approved — finish those in the per-site Runbook before wave sign-off.
                        </div>
                      )}

                      {/* Sign-off */}
                      <div className="mt-4 rounded-md border border-zinc-800 bg-zinc-950 p-3 flex items-start gap-2" data-testid={`wave-signoff-${w.wave}`}>
                        <ShieldCheck className={`h-4 w-4 mt-0.5 shrink-0 ${w.signed_off ? "text-emerald-400" : "text-zinc-500"}`} />
                        <div className="flex-1">
                          <div className="text-[12px] text-zinc-300">Wave cutover sign-off</div>
                          {w.signed_off
                            ? <div className="text-[12px] text-emerald-400 mt-1">Signed off by {w.approver || "—"} · {w.signed_at ? new Date(w.signed_at).toLocaleString() : ""}</div>
                            : <div className="text-[12px] text-zinc-500 mt-1">{checked}/{data.template.length} items · {w.sites.filter((s) => s.cutover_approved).length}/{w.sites.length} sites gated</div>}
                        </div>
                        {w.signed_off
                          ? <button onClick={() => reopen(w.wave)} data-testid={`wave-reopen-${w.wave}`} className="no-print text-[11px] px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800">Reopen</button>
                          : <button onClick={() => signoff(w.wave)} disabled={!ready} data-testid={`wave-signoff-btn-${w.wave}`} className="no-print text-[11px] px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium">Sign off wave</button>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
